import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { entities, getDisplayMessage } from "@/api/apiClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Building2,
  Briefcase,
  Cloud,
  ExternalLink,
  FileText,
  Gavel,
  Globe,
  Heart,
  Landmark,
  Link2,
  Monitor,
  Pencil,
  Router,
  Server,
  Search,
  ShoppingCart,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ICON_MAP = {
  Globe,
  Building2,
  Landmark,
  FileText,
  ShoppingCart,
  Gavel,
  Briefcase,
  Monitor,
  Link2,
  ExternalLink,
  Router,
  Server,
  Cloud,
};

const ICON_KEYS = Object.keys(ICON_MAP);

function IconFor({ name, className }) {
  const Cmp = ICON_MAP[name] || Globe;
  return <Cmp className={className} aria-hidden />;
}

export default function PortaisLicitacaoDialog({ open, onOpenChange }) {
  const queryClient = useQueryClient();
  const [nome, setNome] = useState("");
  const [url, setUrl] = useState("");
  const [icone, setIcone] = useState("Globe");
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [buscaInput, setBuscaInput] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [apenasFavoritos, setApenasFavoritos] = useState(false);
  const [cadastrarComoFavorito, setCadastrarComoFavorito] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setBuscaDebounced(buscaInput.trim());
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [buscaInput]);

  const { data: portais = [], isLoading } = useQuery({
    queryKey: ["portais-licitacao", buscaDebounced, apenasFavoritos],
    queryFn: () =>
      entities.PortalLicitacao.list({
        q: buscaDebounced || undefined,
        favorito: apenasFavoritos,
      }),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setNome("");
      setUrl("");
      setIcone("Globe");
      setEditingId(null);
      setDeleteTarget(null);
      setBuscaInput("");
      setBuscaDebounced("");
      setApenasFavoritos(false);
      setCadastrarComoFavorito(false);
    }
  }, [open]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["portais-licitacao"] });

  const createMutation = useMutation({
    mutationFn: (payload) => entities.PortalLicitacao.create(payload),
    onSuccess: () => {
      toast.success("Portal adicionado.");
      setNome("");
      setUrl("");
      setIcone("Globe");
      setCadastrarComoFavorito(false);
      invalidate();
    },
    onError: (err) => {
      toast.error(getDisplayMessage(err, "Não foi possível adicionar o portal."));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => entities.PortalLicitacao.update(id, data),
    onSuccess: () => {
      toast.success("Portal atualizado.");
      setNome("");
      setUrl("");
      setIcone("Globe");
      setEditingId(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(getDisplayMessage(err, "Não foi possível atualizar o portal."));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => entities.PortalLicitacao.delete(id),
    onSuccess: () => {
      toast.success("Portal removido.");
      setDeleteTarget(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(getDisplayMessage(err, "Não foi possível remover o portal."));
    },
  });

  const toggleFavoritoMutation = useMutation({
    mutationFn: ({ id, favorito }) =>
      entities.PortalLicitacao.update(id, { favorito }),
    onSuccess: () => invalidate(),
    onError: (err) => {
      toast.error(getDisplayMessage(err, "Não foi possível atualizar o favorito."));
    },
  });

  const sortedPortais = useMemo(() => {
    if (!Array.isArray(portais)) return [];
    return [...portais].sort((a, b) => {
      const oa = a.ordem ?? 0;
      const ob = b.ordem ?? 0;
      if (oa !== ob) return oa - ob;
      const ca = new Date(a.createdAt || a.created_at || 0).getTime();
      const cb = new Date(b.createdAt || b.created_at || 0).getTime();
      return ca - cb;
    });
  }, [portais]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { nome: nome.trim(), url: url.trim(), icone };
    if (!payload.nome) {
      toast.error("Informe o nome do portal.");
      return;
    }
    if (!payload.url) {
      toast.error("Informe a URL.");
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate({ ...payload, favorito: cadastrarComoFavorito });
    }
  };

  const startEdit = (p, ev) => {
    ev.stopPropagation();
    setEditingId(p.id);
    setNome(p.nome || "");
    setUrl(p.url || "");
    const k = p.icone && ICON_MAP[p.icone] ? p.icone : "Globe";
    setIcone(k);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setNome("");
    setUrl("");
    setIcone("Globe");
  };

  const openPortal = (href) => {
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto gap-4">
          <DialogHeader>
            <DialogTitle>Portais de licitação</DialogTitle>
            <DialogDescription>
              Cadastre atalhos para sites de pregão e compras. Clique no card para abrir no navegador.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Label className="text-muted-foreground">Consulta</Label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={buscaInput}
                  onChange={(e) => setBuscaInput(e.target.value)}
                  placeholder="Buscar por nome ou URL..."
                  className="pl-9"
                  aria-label="Buscar portais"
                />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Checkbox
                  id="portais-apenas-favoritos"
                  checked={apenasFavoritos}
                  onCheckedChange={(v) => setApenasFavoritos(v === true)}
                />
                <Label htmlFor="portais-apenas-favoritos" className="text-sm font-normal cursor-pointer whitespace-nowrap">
                  Somente favoritos
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground">Seus atalhos</Label>
            {isLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Carregando…</p>
            ) : sortedPortais.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {buscaDebounced || apenasFavoritos
                  ? "Nenhum portal encontrado para os filtros atuais."
                  : "Nenhum portal cadastrado. Preencha o formulário abaixo para adicionar."}
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {sortedPortais.map((p) => (
                  <Card
                    key={p.id}
                    className="border-slate-200 shadow-sm overflow-hidden"
                  >
                    <CardContent className="p-0">
                      <button
                        type="button"
                        className="w-full text-left p-4 pt-3 hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => openPortal(p.url)}
                      >
                        <div className="flex flex-col items-center gap-2 text-center">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                            <IconFor name={p.icone} className="h-6 w-6" />
                          </div>
                          <span className="text-sm font-medium text-slate-900 line-clamp-2">
                            {p.nome}
                          </span>
                        </div>
                      </button>
                      <div className="flex justify-end gap-1 px-2 pb-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "h-8 w-8",
                            p.favorito && "text-rose-600 hover:text-rose-700"
                          )}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            toggleFavoritoMutation.mutate({
                              id: p.id,
                              favorito: !p.favorito,
                            });
                          }}
                          disabled={
                            toggleFavoritoMutation.isPending &&
                            toggleFavoritoMutation.variables?.id === p.id
                          }
                          aria-label={p.favorito ? `Remover ${p.nome} dos favoritos` : `Favoritar ${p.nome}`}
                          aria-pressed={!!p.favorito}
                        >
                          <Heart
                            className={cn("h-4 w-4", p.favorito && "fill-current")}
                          />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(ev) => startEdit(p, ev)}
                          aria-label={`Editar ${p.nome}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setDeleteTarget(p);
                          }}
                          aria-label={`Excluir ${p.nome}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 border-t border-border pt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="portal-nome">Nome</Label>
                <Input
                  id="portal-nome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex.: Comprasnet"
                  maxLength={255}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="portal-url">Endereço (URL)</Label>
                <Input
                  id="portal-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  type="url"
                  autoComplete="url"
                />
              </div>
            </div>
            {!editingId && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="portal-favorito-novo"
                  checked={cadastrarComoFavorito}
                  onCheckedChange={(v) => setCadastrarComoFavorito(v === true)}
                />
                <Label htmlFor="portal-favorito-novo" className="text-sm font-normal cursor-pointer">
                  Cadastrar como favorito
                </Label>
              </div>
            )}
            <div className="space-y-2">
              <Label>Ícone</Label>
              <div className="flex flex-wrap gap-2">
                {ICON_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setIcone(key)}
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-md border transition-colors",
                      icone === key
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background hover:bg-muted"
                    )}
                    title={key}
                    aria-label={`Ícone ${key}`}
                    aria-pressed={icone === key}
                  >
                    <IconFor name={key} className="h-5 w-5" />
                  </button>
                ))}
              </div>
            </div>
            <DialogFooter className="gap-2 sm:justify-start">
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingId ? "Salvar alterações" : "Adicionar portal"}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={cancelEdit}>
                  Cancelar edição
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => {
          if (!v) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover portal?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `O atalho "${deleteTarget.nome}" será removido. Esta ação não pode ser desfeita.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget?.id) deleteMutation.mutate(deleteTarget.id);
              }}
            >
              Remover
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
